/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/gatewaySipProfileApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API functions for Gateway SIP Profiles.
    Centralizes all API calls related to Gateway SIP Profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  ListGatewaySipProfilesResponse,
  GatewaySipProfileResponse,
  GatewaySipProfilePayload,
} from '../types';

/**
 * Fetch list of gateway SIP profiles
 */
export async function fetchGatewaySipProfiles(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number | null;
}): Promise<ListGatewaySipProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId !== undefined && params.domainId !== null && { domainId: params.domainId.toString() }),
  });

  const response = await fetch(
    buildApiUrl(`/api/telephony/gateway-sip-profiles?${queryParams}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch gateway SIP profiles');
  }

  return await response.json();
}

/**
 * Fetch single gateway SIP profile by ID
 */
export async function fetchGatewaySipProfile(id: string | number): Promise<GatewaySipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/gateway-sip-profiles/${id}`),
    {
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch gateway SIP profile');
  }

  return await response.json();
}

/**
 * Create gateway SIP profile
 */
export async function createGatewaySipProfile(
  payload: GatewaySipProfilePayload
): Promise<GatewaySipProfileResponse> {
  const response = await fetch(
    buildApiUrl('/api/telephony/gateway-sip-profiles'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to create gateway SIP profile');
  }

  return await response.json();
}

/**
 * Update gateway SIP profile
 */
export async function updateGatewaySipProfile(
  id: string | number,
  payload: GatewaySipProfilePayload
): Promise<GatewaySipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/gateway-sip-profiles/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to update gateway SIP profile');
  }

  return await response.json();
}

/**
 * Delete gateway SIP profile
 */
export async function deleteGatewaySipProfile(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/gateway-sip-profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to delete gateway SIP profile');
  }
}

/**
 * Duplicate gateway SIP profile
 */
export async function duplicateGatewaySipProfile(
  id: string | number,
  name: string
): Promise<GatewaySipProfileResponse> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/gateway-sip-profiles/${id}/duplicate`),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({ name }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to duplicate gateway SIP profile');
  }

  return await response.json();
}


